import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FolderOpen, Trash2, Download, Image as ImageIcon, Type, Search, Play, Film } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReelPlayer from "@/components/ReelPlayer";

export default function Library() {
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/library").then((r) => setAssets(r.data.assets)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => { await api.delete(`/assets/${id}`); toast.success("Supprimé"); load(); };
  const download = (a) => { const el = document.createElement("a"); el.href = a.data_url; el.download = `monaga-${a.asset_id}.png`; el.click(); };

  const filtered = assets.filter((a) => (filter === "all" || a.type === filter) && (!q || (a.prompt || a.product_name || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-3xl font-bold flex items-center gap-2"><FolderOpen className="text-primary" /> Bibliothèque cloud</h1><p className="text-muted-foreground">Tous vos assets créés, classés et sécurisés.</p></div>

      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="flex gap-2">
          {[{ id: "all", l: "Tout" }, { id: "image", l: "Visuels" }, { id: "video", l: "Vidéos" }, { id: "copy", l: "Textes" }].map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} className={`px-4 py-2 rounded-xl text-sm font-medium border ${filter === f.id ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`library-filter-${f.id}`}>{f.l}</button>
          ))}
        </div>
        <div className="relative"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9 h-10 rounded-xl w-full sm:w-64" data-testid="library-search" /></div>
      </div>

      {filtered.length === 0 ? (
        <div className="p-16 rounded-2xl border border-dashed border-border bg-card text-center text-muted-foreground"><FolderOpen size={44} className="mx-auto mb-3 opacity-40" /><p>Aucun asset. Générez vos premières publicités dans le Studio IA.</p></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((a) => (
            <div key={a.asset_id} className="rounded-2xl border border-border bg-card overflow-hidden group" data-testid={`asset-${a.asset_id}`}>
              {a.type === "image" ? (
                <div className="aspect-square overflow-hidden relative">
                  <img src={a.data_url} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button onClick={() => download(a)} className="h-9 w-9 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center text-white"><Download size={16} /></button>
                    <button onClick={() => remove(a.asset_id)} className="h-9 w-9 rounded-lg bg-destructive/80 flex items-center justify-center text-white"><Trash2 size={16} /></button>
                  </div>
                </div>
              ) : a.type === "video" ? (
                <button onClick={() => setPreview(a)} className="aspect-square overflow-hidden relative w-full" data-testid={`reel-open-${a.asset_id}`}>
                  <img src={a.data_url} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="h-12 w-12 rounded-full bg-white/25 backdrop-blur flex items-center justify-center"><Play size={22} className="text-white ml-0.5" /></div>
                  </div>
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/60 text-white text-[10px] font-semibold flex items-center gap-1"><Film size={10} /> Reel {a.format}</span>
                  <span onClick={(e) => { e.stopPropagation(); remove(a.asset_id); }} className="absolute top-2 right-2 h-7 w-7 rounded-lg bg-destructive/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><Trash2 size={14} /></span>
                </button>
              ) : (
                <div className="aspect-square p-4 bg-gradient-to-br from-indigo-500/10 to-cyan-500/5 flex flex-col relative">
                  <Type size={20} className="text-primary mb-2" />
                  <p className="text-xs font-semibold line-clamp-1">{a.product_name}</p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-4">{a.variants?.[0]?.headline}</p>
                  <button onClick={() => remove(a.asset_id)} className="absolute top-2 right-2 h-7 w-7 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
                </div>
              )}
              <div className="p-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {a.tags?.slice(0, 2).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-md bg-secondary text-muted-foreground">{t}</span>)}
                  {a.ai_score && <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-500 font-semibold ml-auto">{a.ai_score}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="reel-preview-dialog">
          <DialogHeader><DialogTitle>{preview?.title || "Montage vidéo"}</DialogTitle></DialogHeader>
          {preview && (
            <ReelPlayer
              images={preview.frames || [preview.data_url]}
              caption={preview.caption ? preview.caption.split("\n").filter(Boolean) : ""}
              format={preview.format || "9:16"}
              speed={preview.speed || "normal"}
              maxWidth={preview.format === "16:9" ? 440 : preview.format === "1:1" ? 340 : 260}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
